import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import useAutoFocus from '../../../hooks/useAutoFocus';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_ARRAY = Object.freeze([]);

/**
 * Captures the free text the backend REQUIRES before a decision can be recorded.
 *
 * F-06 / REQ-6096 (#603, D-09) — rejecting needs a `reason`. An auditor reading
 * "not approved" with no rationale has a finding, and the author receiving it has
 * nothing actionable. The core raises and the route 400s without one, so before this
 * dialog existed the merged backend made the Approved -> Not approved click simply
 * fail: the pill appeared to do nothing and the error surfaced as a toast.
 *
 * F-08 / REQ-6098 (#603, D-08) — approving OVER another reviewer's open rejection
 * needs a `justification`, because that approval supersedes a live objection. The
 * objection is retained either way (F-09 / REQ-6099); this is the record of why it
 * was overruled, and by whom.
 *
 * Non-empty is enforced here as well as server-side, so the reviewer is told before
 * the round trip rather than after a 400 — but the SERVER remains the authority; this
 * is a courtesy, not the control.
 */
function ApprovalReasonDialog({ open, mode, itemTitle, objectors = EMPTY_ARRAY, onCancel, onConfirm }) {
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  // #793: reuses the SAME ref for the empty-on-submit focus() below AND the
  // reopen-focuses-the-field behaviour `autoFocus` used to (incompletely,
  // first-mount-only) provide — one ref, not two. See hooks/useAutoFocus.
  const inputRef = useAutoFocus(open);

  useEffect(() => {
    if (open) { setText(''); setTouched(false); }
  }, [open]);

  const isReject = mode === 'reject';
  const empty = text.trim().length === 0;

  const submit = () => {
    setTouched(true);
    if (empty) { if (inputRef.current) inputRef.current.focus(); return; }
    onConfirm(text.trim());
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth
            data-testid="approval-reason-dialog">
      <DialogTitle>
        {isReject ? 'Reason for rejection' : 'Justification to approve over an objection'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
          {isReject
            ? `Recorded with your decision on “${itemTitle}” and shown to whoever raised it.`
            : `“${itemTitle}” already carries an open rejection${
              objectors.length ? ` from ${objectors.join(', ')}` : ''
            }. Approving supersedes it — both decisions stay on the record.`}
        </Typography>
        <TextField
          inputRef={inputRef}
          multiline
          minRows={3}
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && empty}
          helperText={touched && empty
            ? (isReject ? 'A reason is required to reject.' : 'A justification is required.')
            : ' '}
          placeholder={isReject
            ? 'What is wrong, and what would make this acceptable?'
            : 'Why does the approval stand despite the objection?'}
          /* #646: `inputProps` is NOT forwarded to the <textarea> under @mui/material
             v9's slot-based TextField — it is spread onto a DOM node literally and
             React drops it, so this test hook never reached the DOM. `slotProps.
             htmlInput` is the v9 spelling, already the convention everywhere else here
             (RequirementsTreeGrid, NewProjectDialog, ApiKeySettings); these two
             approval dialogs were the outliers. */
          slotProps={{ htmlInput: { 'data-testid': 'approval-reason-input' } }}
        />
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onCancel} data-testid="approval-reason-cancel">Cancel</Button>
        <Button
          variant="contained"
          color={isReject ? 'error' : 'primary'}
          onClick={submit}
          disabled={empty}
          data-testid="approval-reason-confirm"
        >
          {isReject ? 'Reject' : 'Approve over objection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ApprovalReasonDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['reject', 'override']).isRequired,
  itemTitle: PropTypes.string,
  objectors: PropTypes.arrayOf(PropTypes.string),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ApprovalReasonDialog;
